import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, btnP, inpS, bdg, sp, rad, type } from "../../constants/styles";
import Ic from "../common/Ic";
import { formatPropAddr } from "../../utils/propertyAddr";
import { withDistances, sortByDistance, filterByRadius, formatDistance } from "../../utils/geo";
import useLocationFilter from "../../hooks/useLocationFilter";
import LocationBar from "../common/LocationBar";
import { COM_TYPES, isCommercial, ptLabel, ptShort } from "../../utils/propertyTypes";
import { RatingSummary, LandlordReviews } from "../common/Reviews";

// --- Map constants (reused from MapViewPage) ---
const SIVAKASI = [9.4533, 77.8024];
const TILE_URL = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>';

// Short marker labels: commercial via the shared ptShort(), residential
// kept local (the marketplace uses "House" rather than "Independent House").
const RES_LABELS = {
  apartment: "Apartment", independent_house: "House", villa: "Villa", pg_room: "PG Room", residential: "Residential"
};
// Accent colors — keep in sync with makeIcon()
const COMM_COLOR = "#8b5cf6"; // purple — commercial
const RES_COLOR = "#22c55e";  // green  — residential
const typeLabel = (pt) => ptShort(pt) || RES_LABELS[pt] || pt || "Residential";

function PublicBrowsePage({ onBack, isLoggedIn, initialPropertyId }) {
  const { t, h } = useContext(Ctx);
  // Google user profile (if signed in via social auth)
  const googleUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("googleUser") || "null"); } catch { return null; }
  }, []);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("list"); // "list" or "map"
  const [mapPreview, setMapPreview] = useState(null);
  const [showTab, setShowTab] = useState("all"); // "all" or "saved"

  // Preference filters
  const [prefRooms, setPrefRooms] = useState(0);
  const [prefMaxRent, setPrefMaxRent] = useState("");
  const [prefFurnishing, setPrefFurnishing] = useState("any");
  const [prefPropType, setPrefPropType] = useState("any");
  const [prefCommType, setPrefCommType] = useState("any");

  // Favorites state
  const [favorites, setFavorites] = useState([]);
  const [favLoaded, setFavLoaded] = useState(false);
  const [favToast, setFavToast] = useState(null);

  // Preference restore flag
  const prefsRestored = useRef(false);

  // Leaflet lazy-load (reused pattern from MapViewPage)
  const [leafletReady, setLeafletReady] = useState(false);
  const leafletModules = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const loadLeaflet = async () => {
      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css";
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }
      try {
        const [L, RL] = await Promise.all([
          import("leaflet"),
          import("react-leaflet")
        ]);
        if (!cancelled) {
          leafletModules.current = { L: L.default || L, ...RL };
          setLeafletReady(true);
        }
      } catch (e) {
        console.error("Leaflet load error", e);
      }
    };
    loadLeaflet();
    return () => { cancelled = true; };
  }, []);

  // Load favorites + preferences on mount for signed-in users
  useEffect(() => {
    if (!isLoggedIn) return;
    // Load favorites
    api.getFavorites().then(data => {
      if (data?.success) setFavorites(data.favorites || []);
      setFavLoaded(true);
    });
    // Load and restore preferences
    if (!prefsRestored.current) {
      api.getPreferences().then(data => {
        if (data?.success && data.preferences) {
          const p = data.preferences;
          if (p.rooms) setPrefRooms(p.rooms);
          if (p.max_rent) setPrefMaxRent(String(p.max_rent));
          if (p.furnishing && p.furnishing !== 'any') setPrefFurnishing(p.furnishing);
          if (p.property_type && p.property_type !== 'any') setPrefPropType(p.property_type);
          if (p.comm_type && p.comm_type !== 'any') setPrefCommType(p.comm_type);
          if (p.view === 'map' || p.view === 'list') setView(p.view);
          prefsRestored.current = true;
        }
      });
    }
  }, [isLoggedIn]);

  // Auto-save preferences when they change (debounced)
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!isLoggedIn || !prefsRestored.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.savePreferences({
        rooms: prefRooms || null,
        max_rent: prefMaxRent ? parseInt(prefMaxRent) : null,
        furnishing: prefFurnishing !== 'any' ? prefFurnishing : null,
        property_type: prefPropType !== 'any' ? prefPropType : null,
        comm_type: prefCommType !== 'any' ? prefCommType : null,
        view
      });
    }, 1500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [isLoggedIn, prefRooms, prefMaxRent, prefFurnishing, prefPropType, prefCommType, view]);

  const fetchProps = useCallback(async () => {
    setLoading(true);
    const prefs = {};
    if (prefRooms > 0) prefs.rooms = prefRooms;
    if (prefMaxRent) prefs.max_rent = parseInt(prefMaxRent);
    if (prefFurnishing !== "any") prefs.furnishing = prefFurnishing;
    if (prefPropType !== "any") prefs.property_type = prefPropType;
    if (prefCommType !== "any") prefs.comm_type = prefCommType;
    const data = await api.getPublicProperties(prefs);
    if (data.success) setProperties(data.properties);
    setLoading(false);
  }, [prefRooms, prefMaxRent, prefFurnishing, prefPropType, prefCommType]);

  useEffect(() => { fetchProps(); }, [fetchProps]);

  // Deep-link: when a ?property=ID URL was shared, auto-open that listing
  // once the property list has loaded.
  useEffect(() => {
    if (!initialPropertyId || !properties.length || selected) return;
    const match = properties.find(p => String(p.id) === String(initialPropertyId));
    if (match) setSelected(match);
  }, [initialPropertyId, properties, selected]);

  // Pending-inquiry resume: when the user submitted an inquiry on a
  // property before signing in, PropertyDetail stashed the form state
  // in localStorage and we redirected them to login. After they sign
  // in with Google, App.js routes them back here with googleUser set
  // and isLoggedIn=true. Detect that resume state once properties have
  // loaded, then reopen the same property and pass the saved form
  // state down to PropertyDetail so it auto-submits the lead. We
  // expire entries older than 10 min so a stale resume from a previous
  // browsing session doesn't fire days later.
  const [pendingResume, setPendingResume] = useState(null);
  useEffect(() => {
    if (!googleUser || properties.length === 0 || pendingResume) return;
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem("pendingInquiry") || "null"); } catch { saved = null; }
    if (!saved || !saved.property_id) return;
    if (saved.savedAt && Date.now() - saved.savedAt > 10 * 60 * 1000) {
      try { localStorage.removeItem("pendingInquiry"); } catch (_) {}
      return;
    }
    const target = properties.find(p => p.id === saved.property_id);
    if (!target) {
      // Property is gone (delisted) — clear the stash, don't loop forever.
      try { localStorage.removeItem("pendingInquiry"); } catch (_) {}
      return;
    }
    setPendingResume(saved);
    setSelected(target);
  }, [googleUser, properties, pendingResume]);

  // Triggered from PropertyDetail's sign-in modal — the user opted to
  // sign in. The sign-in flow lives on the public landing / LoginPage;
  // calling onBack returns them there, and after Google sign-in App.js
  // re-routes them back to PublicBrowsePage with role="user". The
  // pendingInquiry localStorage row keyed by property_id rides through
  // that round-trip and the resume effect above handles re-opening.
  const handleRequestSignIn = useCallback(() => {
    onBack();
  }, [onBack]);

  // Toggle favorite handler
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const toggleFav = async (propertyId, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!isLoggedIn) {
      setShowSignInPrompt(true);
      return;
    }
    // Optimistic update
    const wasFav = favorites.includes(propertyId);
    setFavorites(prev => wasFav ? prev.filter(id => id !== propertyId) : [...prev, propertyId]);
    setFavToast(wasFav ? (t.removedFromFavorites || "Removed from saved") : (t.addedToFavorites || "Saved!"));
    setTimeout(() => setFavToast(null), 1800);
    const res = await api.toggleFavorite(propertyId);
    if (!res?.success) {
      // Revert on failure
      setFavorites(prev => wasFav ? [...prev, propertyId] : prev.filter(id => id !== propertyId));
    }
  };

  const isFav = (id) => favorites.includes(id);

  const hasPrefs = prefRooms > 0 || prefMaxRent || prefFurnishing !== "any";

  // Filter by tab — only show available (vacant) properties to public users
  const displayProps = showTab === "saved" ? properties.filter(p => isFav(p.id)) : properties;
  const vacantRaw = displayProps.filter(p => !p.occ);

  // ── Location filter (shared hook) ──────────────────────────────
  // Same hook powering the tenant Map View and AdminProperties. Owns
  // GPS / text-search / radius / recent state + persistence.
  // userLocation / radiusKm are aliased to the hook so the rest of
  // this large file can keep referring to them by their existing
  // names.
  const locFilter = useLocationFilter({ storageKey: "userLocation" });
  const userLocation = locFilter.userLocation;
  const radiusKm = locFilter.radiusKm;

  // Fullscreen map toggle — see MapViewPage for rationale.
  const [fullscreenMap, setFullscreenMap] = useState(false);

  // Auto-pan the map when the user picks a new reference point while
  // looking at the map view. Leaflet ignores the center prop after
  // first mount, so push setView() imperatively.
  useEffect(() => {
    if (view !== "map") return;
    if (!locFilter.userLocation || !mapRef.current?.setView) return;
    try {
      mapRef.current.setView([locFilter.userLocation.lat, locFilter.userLocation.lng], 14);
    } catch (_) { /* map not ready yet */ }
  }, [locFilter.userLocation, view]);

  // Leaflet invalidateSize() after fullscreen toggle. Same rationale
  // as MapViewPage: container size changes → tiles must recompute.
  useEffect(() => {
    if (view !== "map" || !mapRef.current?.invalidateSize) return;
    const id = setTimeout(() => {
      try { mapRef.current.invalidateSize(); } catch (_) {}
    }, 50);
    return () => clearTimeout(id);
  }, [fullscreenMap, view]);

  // Compose: enrich with _distance_km if we have a fix, then apply
  // radius filter, then sort nearest-first when a fix is present.
  // Without a fix, fall through to the existing order (relevance/recency).
  const vacant = useMemo(() => {
    if (!userLocation) return vacantRaw;
    const enriched = withDistances(vacantRaw, userLocation);
    const filtered = filterByRadius(enriched, radiusKm);
    return sortByDistance(filtered);
  }, [vacantRaw, userLocation, radiusKm]);

  // Geo-enabled properties for map — only vacant
  const geoProps = useMemo(() => vacant.filter(p => p.latitude && p.longitude), [vacant]);
  const hasGeoData = useMemo(() => vacant.some(p => p.latitude && p.longitude), [vacant]);

  // Marker icon with price for vacant properties
  const makeIcon = (occ, fav, rent, propertyType) => {
    if (!leafletModules.current) return null;
    const L = leafletModules.current.L;
    // Favorited: red heart pin
    if (fav) {
      return L.divIcon({ className: "", iconSize: [28, 28], iconAnchor: [14, 14],
        html: `<div style="width:28px;height:28px;border-radius:50%;background:#ff375f;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center"><span style="color:#fff;font-size:12px">\u2665</span></div>` });
    }
    // Vacant with price: GREEN (residential) or PURPLE (commercial) bubble
    if (!occ && rent) {
      const priceLabel = rent >= 1000 ? `${Math.round(rent / 1000)}K` : `${rent}`;
      const isComm = isCommercial(propertyType);
      const bgColor = isComm ? "#8b5cf6" : "#22c55e";
      const typeLabel = isComm ? (ptShort(propertyType) || "Commercial") : "";
      const label = typeLabel ? `${typeLabel} \u00B7 \u20B9${priceLabel}` : `\u20B9${priceLabel}`;
      const width = isComm ? 100 : 52;
      return L.divIcon({ className: "", iconSize: [width, 28], iconAnchor: [width / 2, 28],
        html: `<div style="background:${bgColor};color:#fff;font-size:10px;font-weight:700;padding:4px 8px;border-radius:12px;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.3);border:2px solid #fff;text-align:center">${label}</div>` });
    }
    // Vacant without price: ORANGE bubble
    if (!occ) {
      return L.divIcon({ className: "", iconSize: [48, 28], iconAnchor: [24, 28],
        html: `<div style="background:#f59e0b;color:#fff;font-size:10px;font-weight:700;padding:4px 8px;border-radius:12px;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.3);border:2px solid #fff;text-align:center">Vacant</div>` });
    }
    // Occupied: small gray dot, very dimmed
    return L.divIcon({ className: "", iconSize: [14, 14], iconAnchor: [7, 7],
      html: `<div style="width:14px;height:14px;border-radius:50%;background:#8e8e93;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.2);opacity:0.4"></div>` });
  };

  // Wrap both views in a fragment so the favorites sign-in modal (rendered
  // at the bottom of this function) is visible from EITHER the list view
  // OR the PropertyDetail view. Previously the modal lived inside the list
  // view's <div>, so tapping the heart inside PropertyDetail flipped
  // `showSignInPrompt` to true but the modal never painted — the user only
  // saw it after going back to the list. Hoisting the modal out fixes that
  // without duplicating the JSX inside PropertyDetail.
  if (selected) return <>
    <PropertyDetail property={selected} onBack={() => { setSelected(null); setPendingResume(null); }} t={t} h={h}
      prefs={{ rooms: prefRooms, max_rent: prefMaxRent ? parseInt(prefMaxRent) : 0, furnishing: prefFurnishing, move_in: "" }}
      googleUser={googleUser} isFav={isFav(selected.id)} toggleFav={toggleFav} isLoggedIn={isLoggedIn}
      onRequestSignIn={handleRequestSignIn}
      pendingResume={pendingResume && pendingResume.property_id === selected.id ? pendingResume : null} />
    {/* Favorites sign-in prompt — same modal as the list view. Lives at
        the top level so a heart-tap inside PropertyDetail surfaces it
        immediately instead of waiting until the user navigates back. */}
    {showSignInPrompt && <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: h.cd, borderRadius: 16, padding: 24, maxWidth: 320, width: "100%", textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,.2)" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>{"❤️"}</div>
        <div style={{ color: h.tx, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{t.signInToSave || "Sign in to Save Favorites"}</div>
        <div style={{ color: h.ts, fontSize: 13, marginBottom: 20 }}>Sign in with Google to save your favorite properties and get notified about updates.</div>
        <button onClick={() => { setShowSignInPrompt(false); onBack(); }} style={{ width: "100%", padding: "12px 20px", borderRadius: 12, border: "none", background: h.ac, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 8 }}>
          Sign In with Google
        </button>
        <button onClick={() => setShowSignInPrompt(false)} style={{ width: "100%", padding: "10px 20px", borderRadius: 12, border: `1px solid ${h.bd}`, background: "transparent", color: h.ts, fontSize: 13, cursor: "pointer" }}>
          Maybe Later
        </button>
      </div>
    </div>}
  </>;

  return <div style={{ minHeight: "100vh", background: h.bg, padding: 16, maxWidth: 430, margin: "0 auto", position: "relative" }}>
    {/* User profile bar for signed-in Google users */}
    {isLoggedIn && googleUser && <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, padding: "8px 12px", background: h.cd, borderRadius: 12 }}>
      {googleUser.avatar ? <img src={googleUser.avatar} alt="" style={{ width: 32, height: 32, borderRadius: "50%", border: `2px solid ${h.ac}` }} referrerPolicy="no-referrer" />
        : <div style={{ width: 32, height: 32, borderRadius: "50%", background: h.ac, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 14 }}>{(googleUser.name || "U")[0]}</div>}
      <div style={{ flex: 1 }}>
        <div style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>{googleUser.name}</div>
        <div style={{ color: h.sc, fontSize: 11 }}>{googleUser.email}</div>
      </div>
      {favorites.length > 0 && <div style={{ background: `${h.rd}15`, color: h.rd, padding: "4px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 3 }}>
        ♥ {favorites.length}
      </div>}
      <button onClick={onBack} style={{ background: `${h.rd}15`, border: "none", color: h.rd, padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
        {t.logout}
      </button>
    </div>}

    {/* Header with map/list toggle */}
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
      {!isLoggedIn ? <button onClick={onBack} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}>
        <Ic n="back" s={16} c={h.ac} />{t.back}
      </button> : <div style={{ width: 60 }} />}
      <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>{t.browseProperties || "Browse Properties"}</h2>
      {hasGeoData ? <button onClick={() => { setView(view === "list" ? "map" : "list"); setMapPreview(null); }}
        style={{ background: `${h.ac}15`, border: `1px solid ${h.ac}`, color: h.ac, padding: "5px 12px", borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
        {view === "list" ? ("\uD83D\uDDFA " + (t.mapView || "Map")) : ("\u2630 " + (t.listView || "List"))}
      </button> : <div style={{ width: 60 }} />}
    </div>

    {/* All / Saved tabs (only for signed-in users with favorites) */}
    {isLoggedIn && favLoaded && favorites.length > 0 && <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
      {["all", "saved"].map(tab => <button key={tab} onClick={() => setShowTab(tab)} style={{
        flex: 1, padding: "7px 0", borderRadius: 10, cursor: "pointer", fontSize: 12, fontWeight: 600,
        border: showTab === tab ? `1.5px solid ${tab === "saved" ? h.rd : h.ac}` : `1px solid ${h.bd}`,
        background: showTab === tab ? (tab === "saved" ? `${h.rd}10` : `${h.ac}10`) : "transparent",
        color: showTab === tab ? (tab === "saved" ? h.rd : h.ac) : h.sc
      }}>
        {tab === "all" ? (t.all || "All") : `♥ ${t.saved || "Saved"} (${favorites.length})`}
      </button>)}
    </div>}

    {/* Preference filters */}
    <div style={{ ...crd(h), marginBottom: 16, padding: 12 }}>
      <div style={{ color: h.tx, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{t.yourPreferences || "Your Preferences"}</div>
      {/* Row 1: Rooms */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ color: h.sc, fontSize: 10, marginBottom: 4 }}>{t.rooms || "Rooms"}</div>
        <div style={{ display: "flex", gap: 4 }}>
          {[0, 1, 2, 3, 4, 5].map(r => <button key={r} onClick={() => setPrefRooms(r === prefRooms ? 0 : r)} style={{
            padding: "5px 12px", borderRadius: 12, cursor: "pointer", fontSize: 11, fontWeight: prefRooms === r ? 600 : 400,
            border: `1px solid ${prefRooms === r ? h.ac : h.bd}`,
            background: prefRooms === r ? `${h.ac}15` : "transparent",
            color: prefRooms === r ? h.ac : h.sc
          }}>{r === 0 ? (t.any || "Any") : r}</button>)}
        </div>
      </div>
      {/* Row 2: Max Budget + Furnishing */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <div style={{ color: h.sc, fontSize: 10, marginBottom: 4 }}>{t.maxBudget || "Max Budget"}</div>
          <input value={prefMaxRent} onChange={e => setPrefMaxRent(e.target.value.replace(/\D/g, ''))}
            placeholder={"\u20B9 Max"} type="text" style={{ ...inpS(h), padding: "6px 10px", fontSize: 11, margin: 0, width: "100%", boxSizing: "border-box" }} />
        </div>
        <div>
          <div style={{ color: h.sc, fontSize: 10, marginBottom: 4 }}>{t.furnishing || "Furnishing"}</div>
          <select value={prefFurnishing} onChange={e => setPrefFurnishing(e.target.value)}
            style={{ ...inpS(h), padding: "6px 8px", fontSize: 11, margin: 0, width: "100%", boxSizing: "border-box" }}>
            <option value="any">{t.any || "Any"}</option>
            <option value="furnished">{t.furnished || "Furnished"}</option>
            <option value="semi">{t.semiFurnished || "Semi"}</option>
            <option value="unfurnished">{t.unfurnished || "Unfurnished"}</option>
          </select>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: h.sc, fontSize: 10, marginBottom: 2 }}>{t.propertyType || "Property Type"}</div>
          <select value={prefPropType} onChange={e => { setPrefPropType(e.target.value); if (e.target.value !== "any") setPrefCommType("any"); }}
            style={{ ...inpS(h), padding: "4px 6px", fontSize: 10, margin: 0, width: "100%" }}>
            <option value="any">{t.any || "Any"}</option>
            <option value="apartment">{t.apartment || "Apartment"}</option>
            <option value="independent_house">{t.independentHouse || "Independent House"}</option>
            <option value="villa">{t.villa || "Villa"}</option>
            <option value="pg_room">{t.pgRoom || "PG Room"}</option>
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: h.sc, fontSize: 10, marginBottom: 2 }}>{t.commercialType || "Commercial Type"}</div>
          <select value={prefCommType} onChange={e => { setPrefCommType(e.target.value); if (e.target.value !== "any") setPrefPropType("any"); }}
            style={{ ...inpS(h), padding: "4px 6px", fontSize: 10, margin: 0, width: "100%" }}>
            <option value="any">{t.any || "Any"}</option>
            {COM_TYPES.map(ct => <option key={ct} value={ct}>{ptLabel(ct, t)}</option>)}
          </select>
        </div>
      </div>
    </div>

    {/* Bar buttons are duplicate of the map's floating buttons when
        view === 'map', so hide them in that case. In list view the
        bar is the only entry point for location filtering — both
        buttons must be visible. */}
    <LocationBar
      filter={locFilter} h={h} t={t}
      hideSearchButton={view === "map"}
      hideLocationButton={view === "map"}
    />

    {loading ? <div style={{ textAlign: "center", padding: 40 }}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
    </div> : view === "map" ? (
      /* ===== MAP VIEW ===== */
      <div>
        <div style={fullscreenMap ? {
          // No explicit 100vh height — on mobile browsers 100vh includes
          // the area behind the collapsing URL/system bars, pushing the
          // bottom card off-screen. inset:0 alone pins all four edges to
          // the actual visible viewport. Same fix as MapViewPage.
          position: "fixed", inset: 0, zIndex: 99998,
          borderRadius: 0, border: "none",
          overflow: "hidden", background: h.bg,
        } : {
          // zIndex: 0 traps Leaflet's internal z-indexes (up to 1000)
          // inside the card so they can't paint over fixed app chrome.
          // Same fix as MapViewPage.
          position: "relative", zIndex: 0, borderRadius: 14, overflow: "hidden",
          height: "calc(100vh - 280px)", minHeight: 300,
          border: `1px solid ${h.bd}`,
        }}>
          {leafletReady && leafletModules.current ? (() => {
            const { MapContainer, TileLayer, Marker, useMap } = leafletModules.current;
            const FitBounds = ({ points }) => {
              const map = useMap();
              useEffect(() => {
                if (points.length > 0) {
                  const L = leafletModules.current.L;
                  const bounds = L.latLngBounds(points.map(p => [p.latitude, p.longitude]));
                  map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
                }
              }, [points, map]);
              return null;
            };
            // Pulsing-blue "you are here" marker. Visually distinct
            // from property pins (which are green/purple bubbles)
            // so the user can immediately spot themselves on the map.
            // The outer ring uses the `geo-ping` keyframe defined in
            // index.css — it expands + fades outward like Apple Maps.
            const userIcon = userLocation && leafletModules.current.L.divIcon({
              className: "",
              iconSize: [22, 22],
              iconAnchor: [11, 11],
              html: `<div style="position:relative;width:22px;height:22px;">
                <div style="position:absolute;inset:-6px;border-radius:50%;background:#0a84ff44;animation:geo-ping 1.8s cubic-bezier(0,0,.2,1) infinite"></div>
                <div style="position:absolute;inset:0;border-radius:50%;background:#0a84ff;border:3px solid #fff;box-shadow:0 2px 8px rgba(10,132,255,.5)"></div>
              </div>`,
            });
            return <MapContainer ref={mapRef}
              center={userLocation ? [userLocation.lat, userLocation.lng] : (geoProps.length > 0 ? [geoProps[0].latitude, geoProps[0].longitude] : SIVAKASI)}
              zoom={13} style={{ height: "100%", width: "100%" }} zoomControl={false}>
              <TileLayer url={TILE_URL} attribution={TILE_ATTR} />
              {geoProps.length > 1 && <FitBounds points={geoProps} />}
              {geoProps.map(p => <Marker key={p.id}
                position={[p.latitude, p.longitude]}
                icon={makeIcon(p.occ, isFav(p.id), p.rent, p.property_type)}
                /* Tapping a pin auto-enters fullscreen + opens preview.
                   Same UX as MapViewPage — focused immersive view of
                   the property bubble, exit-fullscreen via the ⤡
                   button or by tapping View Details which routes to
                   the PropertyDetail page. */
                eventHandlers={{ click: () => { setMapPreview(p); setFullscreenMap(true); } }}
              />)}
              {userLocation && userIcon && <Marker
                position={[userLocation.lat, userLocation.lng]}
                icon={userIcon}
              />}
            </MapContainer>;
          })() : <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: h.bg }}>
            <div style={{ color: h.sc, fontSize: 13 }}>{t.loadingMap || "Loading map..."}</div>
          </div>}

          {/* Floating ⛶ / ⤡ fullscreen toggle — top-LEFT corner so it
              doesn't fight the 📍 button (top-right). Always visible,
              even with the bottom-sheet preview open, so the user
              can exit fullscreen at any time. */}
          <button
            onClick={() => setFullscreenMap(v => !v)}
            style={{
              position: "absolute",
              top: 12, left: 12,
              zIndex: 1001,
              width: 44, height: 44,
              borderRadius: "50%",
              border: "none",
              background: fullscreenMap ? "#0a84ff" : h.cd,
              color: fullscreenMap ? "#fff" : h.ac,
              cursor: "pointer",
              boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
            }}
            aria-label={fullscreenMap ? "Exit fullscreen" : "Enter fullscreen"}
            title={fullscreenMap ? "Exit fullscreen" : "Enter fullscreen"}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {fullscreenMap ? (
                <>
                  <polyline points="4 14 10 14 10 20" />
                  <polyline points="20 10 14 10 14 4" />
                  <line x1="14" y1="10" x2="21" y2="3" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </>
              ) : (
                <>
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </>
              )}
            </svg>
          </button>

          {/* Floating 🔍 search lens — sits LEFT of the 📍 button so
              both top-right controls form a horizontal pair. Tapping
              opens the fullscreen search modal (in LocationBar) where
              the user types a place name and picks from suggestions /
              recent. Hidden when the preview card is open, same as 📍. */}
          {!mapPreview && (
            <button
              onClick={() => locFilter.setSearchOpen(true)}
              style={{
                position: "absolute",
                top: 12, right: 64,
                zIndex: 1000,
                width: 44, height: 44,
                borderRadius: "50%",
                border: "none",
                background: h.cd,
                color: h.ac,
                cursor: "pointer",
                boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s",
              }}
              aria-label="Search a place"
              title="Search a place"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
          )}

          {/* Floating "📍 My location" button. Tap → either requests
              the user's location (first tap, never auto-prompts) or
              recenters the map on their existing location (subsequent
              taps). Sits top-right where it's reachable on a phone
              without covering map content. Hidden when the preview
              card is open so it doesn't clash with that UI. */}
          {!mapPreview && (
            <button
              onClick={async () => {
                if (!userLocation) {
                  // First tap — request permission + fetch
                  await locFilter.requestLocation();
                  // After requestLocation resolves, the new userLocation
                  // triggers the useMemo for geoProps + the map's
                  // `center` would update on next render. We also pan
                  // explicitly so the experience is instant.
                  try {
                    const fresh = JSON.parse(sessionStorage.getItem('userLocation') || 'null');
                    if (fresh && mapRef.current?.setView) {
                      mapRef.current.setView([fresh.lat, fresh.lng], 14);
                    }
                  } catch (_) {}
                } else {
                  // Subsequent tap — just recenter
                  if (mapRef.current?.setView) {
                    mapRef.current.setView([userLocation.lat, userLocation.lng], 14);
                  }
                }
              }}
              disabled={locFilter.geolocating}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 1000,
                width: 44,
                height: 44,
                borderRadius: "50%",
                border: "none",
                background: userLocation ? "#0a84ff" : h.cd,
                color: userLocation ? "#fff" : h.ac,
                cursor: locFilter.geolocating ? "wait" : "pointer",
                boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
                fontSize: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: locFilter.geolocating ? 0.7 : 1,
                transition: "all 0.2s",
              }}
              aria-label={userLocation ? "Recenter on my location" : "Use my location"}
              title={userLocation ? "Recenter on my location" : "Use my location"}
            >
              {locFilter.geolocating ? "…" : "📍"}
            </button>
          )}

          {/* Bottom-sheet style preview card */}
          {mapPreview && <div style={{ position: "absolute", bottom: "calc(12px + env(safe-area-inset-bottom, 0px))", left: 12, right: 12, background: h.cd, borderRadius: 14, padding: 14, boxShadow: "0 4px 20px rgba(0,0,0,.25)", zIndex: 1000, border: `1px solid ${h.bd}` }}>
            <div style={{ position: "absolute", top: 8, right: 10, display: "flex", gap: 6 }}>
              <button onClick={(e) => toggleFav(mapPreview.id, e)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, padding: 0, lineHeight: 1 }}>
                {isFav(mapPreview.id) ? <span style={{ color: h.rd }}>♥</span> : <span style={{ color: h.sc }}>♡</span>}
              </button>
              <button onClick={() => setMapPreview(null)} style={{ background: "none", border: "none", color: h.sc, fontSize: 16, cursor: "pointer", padding: 0 }}>✕</button>
            </div>
            {mapPreview.org_name && <div style={{ color: h.ac, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2, paddingRight: 50 }}>{(t.listedBy || "Listed by")} {mapPreview.org_name}</div>}
            <div style={{ color: h.tx, fontWeight: 600, fontSize: 13, marginBottom: 4, paddingRight: 50 }}>{formatPropAddr(mapPreview)}</div>
            {(() => {
              const isComm = isCommercial(mapPreview.property_type);
              const tc = isComm ? COMM_COLOR : RES_COLOR;
              return <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                <span style={{ background: tc, color: "#fff", padding: "2px 8px", borderRadius: rad.pill, fontSize: 10, fontWeight: 700 }}>
                  {isComm ? "🏢 " : "🏠 "}{typeLabel(mapPreview.property_type)}
                </span>
                <span style={{ color: h.sc, fontSize: 11 }}>{mapPreview.rooms} {t.rooms || "Rooms"}</span>
                <span style={{ color: h.sc, fontSize: 11 }}>·</span>
                <span style={{ color: tc, fontSize: 13, fontWeight: 700 }}>₹{(mapPreview.rent || 0).toLocaleString()}{t.perMonth || "/mo"}</span>
                {hasPrefs && mapPreview.match_score > 0 && <MatchBadge score={mapPreview.match_score} h={h} />}
              </div>;
            })()}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ padding: "3px 10px", borderRadius: 10, background: `${h.gn}15`, color: h.gn, fontSize: 10, fontWeight: 600 }}>
                {t.available || "Available"}
              </span>
              {/* CTA goes to the PropertyDetail view (photos / amenities / full
                  inquiry form) — labelling this "Inquire Now" was misleading
                  since the user lands on a details page first, not directly
                  on the inquiry form. The real "Inquire Now" CTA lives at the
                  bottom of PropertyDetail. */}
              <button onClick={() => { setSelected(mapPreview); setMapPreview(null); setFullscreenMap(false); }} style={{
                background: h.ac, color: "#fff", border: "none", padding: "7px 16px", borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 600
              }}>{t.viewDetails || "View Details"} →</button>
            </div>
          </div>}
        </div>

        {/* Properties without coordinates — show as list below map */}
        {vacant.filter(p => !p.latitude || !p.longitude).length > 0 && <div style={{ marginTop: 16 }}>
          <div style={{ color: h.sc, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>{t.otherProperties || "Other Properties"}</div>
          {vacant.filter(p => !p.latitude || !p.longitude).map(p =>
            <PropertyCard key={p.id} p={p} h={h} t={t} onClick={() => setSelected(p)} hasPrefs={hasPrefs}
              isFav={isFav(p.id)} onToggleFav={(e) => toggleFav(p.id, e)} />
          )}
        </div>}
      </div>
    ) : (
      /* ===== LIST VIEW ===== */
      <>
        {showTab === "saved" && displayProps.length === 0 && <div style={{ ...crd(h), textAlign: "center", padding: 32 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>♥</div>
          <div style={{ color: h.tx, fontSize: 14, fontWeight: 600 }}>{t.noFavorites || "No saved properties yet"}</div>
          <div style={{ color: h.sc, fontSize: 12, marginTop: 4 }}>{t.noFavoritesDesc || "Tap the heart icon on any property to save it"}</div>
          <button onClick={() => setShowTab("all")} style={{ ...btnP(h), marginTop: 16, fontSize: 12 }}>{t.browseAll || "Browse All"}</button>
        </div>}


        {vacant.length > 0 && <>
          <h3 style={{ color: h.gn, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{t.availableProperties || "Available Properties"} ({vacant.length})</h3>
          {vacant.map(p => <PropertyCard key={p.id} p={p} h={h} t={t} onClick={() => setSelected(p)} hasPrefs={hasPrefs}
            isFav={isFav(p.id)} onToggleFav={(e) => toggleFav(p.id, e)} />)}
        </>}

        {vacant.length === 0 && <div style={{ ...crd(h), textAlign: "center", padding: 32 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏠</div>
          <div style={{ color: h.tx, fontSize: 14, fontWeight: 600 }}>
            {userLocation && radiusKm
              ? (t.noPropsInRadius || `No properties within ${radiusKm} km`)
              : (t.noVacantProps || "No properties available right now")}
          </div>
          <div style={{ color: h.sc, fontSize: 12, marginTop: 6 }}>
            {userLocation && radiusKm
              ? (t.tryWiderRadius || "Try a wider radius or clear your location filter")
              : "Check back later for new listings"}
          </div>
        </div>}
      </>
    )}

    {/* Toast notification */}
    {favToast && <div style={{
      position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: h.cd,
      color: h.tx, padding: "10px 20px", borderRadius: 12, fontSize: 13, fontWeight: 600,
      boxShadow: "0 4px 20px rgba(0,0,0,.3)", border: `1px solid ${h.bd}`, zIndex: 9999,
      animation: "fadeIn .2s ease", whiteSpace: "nowrap"
    }}>{favToast}</div>}

    {/* Sign-in prompt for favorites */}
    {showSignInPrompt && <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: h.cd, borderRadius: 16, padding: 24, maxWidth: 320, width: "100%", textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,.2)" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>{"\u2764\uFE0F"}</div>
        <div style={{ color: h.tx, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{t.signInToSave || "Sign in to Save Favorites"}</div>
        <div style={{ color: h.ts, fontSize: 13, marginBottom: 20 }}>Sign in with Google to save your favorite properties and get notified about updates.</div>
        <button onClick={() => { setShowSignInPrompt(false); onBack(); }} style={{ width: "100%", padding: "12px 20px", borderRadius: 12, border: "none", background: h.ac, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 8 }}>
          Sign In with Google
        </button>
        <button onClick={() => setShowSignInPrompt(false)} style={{ width: "100%", padding: "10px 20px", borderRadius: 12, border: `1px solid ${h.bd}`, background: "transparent", color: h.ts, fontSize: 13, cursor: "pointer" }}>
          Maybe Later
        </button>
      </div>
    </div>}
  </div>;
}

function MatchBadge({ score, h, size = "sm", reasons }) {
  if (!score && score !== 0) return null;
  let color, label, icon;
  if (score >= 80) { color = h.gn || "#30d158"; label = "Great Match"; icon = "check-circle"; }
  else if (score >= 60) { color = h.ac || "#0a84ff"; label = "Good Match"; icon = "target"; }
  else if (score >= 40) { color = h.wn || "#ff9f0a"; label = "Fair Match"; icon = "target"; }
  else { color = h.sc || "#8e8e93"; label = "Low Match"; icon = "target"; }

  // --- Full-width card for detail views ---
  if (size === "lg") {
    return <div style={{
      background: `${color}10`, borderRadius: rad.lg,
      border: `1.5px solid ${color}30`, padding: sp.lg, marginBottom: sp.md
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
        <div style={{
          width: 44, height: 44, borderRadius: rad.sm + 2,
          background: `${color}20`, display: "flex", alignItems: "center",
          justifyContent: "center", flexShrink: 0
        }}>
          <Ic n={icon} s={22} c={color} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
            <span style={{ ...type.h3, color, margin: 0 }}>{label}</span>
            <span style={{
              ...bdg(`${color}20`, color),
              fontSize: 14, fontWeight: 800, padding: "4px 14px"
            }}>{score}%</span>
          </div>
          {/* Score bar */}
          <div style={{ height: 6, borderRadius: 3, background: `${color}15`, marginTop: 6, overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 3, background: color, width: `${score}%`, transition: "width .4s ease" }} />
          </div>
        </div>
      </div>
      {/* Match reasons */}
      {reasons && reasons.length > 0 && <div style={{ marginTop: sp.sm, display: "flex", flexWrap: "wrap", gap: 6 }}>
        {reasons.map((r, i) => <span key={i} style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          background: `${color}12`, color, padding: "4px 10px",
          borderRadius: rad.pill, fontSize: 11, fontWeight: 600
        }}>
          <Ic n="check" s={11} c={color} />{r}
        </span>)}
      </div>}
    </div>;
  }

  // --- Compact badge (for cards and map previews) ---
  return <span style={{
    display: "inline-flex", alignItems: "center", gap: 6,
    background: `${color}15`, color,
    padding: "5px 12px", borderRadius: rad.sm,
    fontSize: 12, fontWeight: 700,
    border: `1px solid ${color}25`
  }}>
    <Ic n={icon} s={14} c={color} />
    <span style={{ fontWeight: 800 }}>{score}%</span>
    <span style={{ fontWeight: 600 }}>{label}</span>
  </span>;
}

function HeartBtn({ isFav, onToggle, h, size = 20 }) {
  return <button onClick={onToggle} style={{
    background: "none", border: "none", cursor: "pointer", padding: 4, lineHeight: 1,
    fontSize: size, transition: "transform .15s", transform: isFav ? "scale(1.1)" : "scale(1)"
  }}>
    {isFav ? <span style={{ color: h.rd || "#ff375f" }}>♥</span> : <span style={{ color: h.sc }}>♡</span>}
  </button>;
}

function PropertyCard({ p, h, t, onClick, dimmed, hasPrefs, isFav, onToggleFav }) {
  const amen = Array.isArray(p.amen) ? p.amen : [];
  const pPhotos = p.photos || [];
  const isComm = isCommercial(p.property_type);
  const typeColor = isComm ? COMM_COLOR : RES_COLOR;
  const hasPhoto = pPhotos.length > 0;
  // Same pill markup used in both positioning modes. Inline-rendered
  // when there's no photo (so it doesn't overlap the "Listed by ..."
  // text); absolutely positioned over the photo otherwise.
  const typeBadge = (
    <span style={{
      background: typeColor, color: "#fff",
      padding: "3px 10px", borderRadius: rad.pill,
      fontSize: 10, fontWeight: 700,
      boxShadow: hasPhoto ? "0 2px 6px rgba(0,0,0,.2)" : "none",
      letterSpacing: 0.2,
      display: "inline-flex", alignItems: "center",
    }}>{isComm ? "🏢 " : "🏠 "}{typeLabel(p.property_type)}</span>
  );

  return <div onClick={onClick} style={{ ...crd(h), cursor: "pointer", opacity: dimmed ? 0.6 : 1, transition: "transform .2s", position: "relative", overflow: "hidden", padding: 0, borderLeft: `3px solid ${typeColor}` }}>
    {/* Photo */}
    {hasPhoto && <img src={pPhotos[0]} alt="" style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }} />}
    {/* Heart button — absolute so it sits over the photo when present
        and over the top-right corner of the content area when not. */}
    <div style={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}>
      <HeartBtn isFav={isFav} onToggle={onToggleFav} h={h} />
    </div>
    {/* Property-type badge ONLY when there's a photo — it sits on top
        of the photo. With no photo, the badge is rendered inline at the
        start of the content area instead so it doesn't overlap the
        "Listed by" text. */}
    {hasPhoto && (
      <span style={{ position: "absolute", top: 10, left: 10, zIndex: 2 }}>
        {typeBadge}
      </span>
    )}
    <div style={{ padding: 14 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", paddingRight: 32 }}>
      <div style={{ flex: 1 }}>
        {!hasPhoto && <div style={{ marginBottom: 6 }}>{typeBadge}</div>}
        {p.org_name && <div style={{ color: h.ac, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>{(t.listedBy || "Listed by")} {p.org_name}</div>}
        <div style={{ color: h.tx, fontWeight: 600, fontSize: 14 }}>{formatPropAddr(p)}</div>
        <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{p.rooms} {t.rooms || "Rooms"}{p.area_sqft ? ` · ${p.area_sqft} sqft` : ""}</div>
        {/* Distance pill — only when the property has been enriched
            via withDistances() AND the value is finite (i.e. the
            property HAS lat/lng). Properties without coords get
            Infinity and we silently omit the pill rather than
            showing a misleading "0 m" or "?". */}
        {p._distance_km != null && Number.isFinite(p._distance_km) && (
          <div style={{
            marginTop: 6,
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            padding: "2px 8px",
            borderRadius: 999,
            background: `${h.ac}10`,
            color: h.ac,
            fontSize: 10,
            fontWeight: 600,
          }}>
            <span style={{ fontSize: 10 }}>{"📍"}</span>
            {formatDistance(p._distance_km)} {t.away || "away"}
          </div>
        )}
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ color: typeColor, fontWeight: 700, fontSize: 16 }}>₹{(p.rent || 0).toLocaleString()}</div>
        <div style={{ color: h.sc, fontSize: 10 }}>{t.perMonth || "/month"}</div>
      </div>
    </div>
    {hasPrefs && p.match_score > 0 && <div style={{ marginTop: 8 }}>
      <MatchBadge score={p.match_score} h={h} />
      {p.match_reasons && p.match_reasons.length > 0 && <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
        {p.match_reasons.slice(0, 3).map((r, i) => {
          const mc = p.match_score >= 60 ? (h.gn || "#30d158") : (h.ac || "#0a84ff");
          return <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 3, background: `${mc}10`, color: mc, padding: "3px 8px", borderRadius: rad.pill, fontSize: 10, fontWeight: 600 }}>
            <Ic n="check" s={10} c={mc} />{r}
          </span>;
        })}
      </div>}
    </div>}
    {/* 2-line teaser of the landlord's description; full text on detail */}
    {p.description && <div style={{ color: h.ts || h.sc, fontSize: 11, marginTop: 8, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.description}</div>}
    {amen.length > 0 && <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
      {amen.map(a => <span key={a} style={{ background: `${h.ac}10`, color: h.ac, padding: "2px 8px", borderRadius: 10, fontSize: 10 }}>{a}</span>)}
    </div>}
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
      <span style={{ padding: "3px 10px", borderRadius: 10, background: `${h.gn}15`, color: h.gn, fontSize: 10, fontWeight: 600 }}>{t.available || "Available"}</span>
      <span style={{ color: h.ac, fontSize: 11, fontWeight: 600 }}>{t.viewDetails || "View Details"} {"\u2192"}</span>
    </div>
    </div>
  </div>;
}

function PropertyDetail({ property, onBack, t, h, prefs, googleUser, isFav, toggleFav, isLoggedIn, onRequestSignIn, pendingResume }) {
  const [name, setName] = useState(googleUser?.name || "");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  // Honeypot — invisible to humans, irresistible to dumb form-fillers.
  // If this comes back non-empty, the server silently 200s without insert.
  const [website, setWebsite] = useState("");
  const [prefRooms, setPrefRooms] = useState(prefs?.rooms || 0);
  const [prefMaxRent, setPrefMaxRent] = useState(prefs?.max_rent || "");
  const [prefFurnishing, setPrefFurnishing] = useState(prefs?.furnishing || "any");
  const [prefMoveIn, setPrefMoveIn] = useState(prefs?.move_in || "");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const p = property;
  const amen = Array.isArray(p.amen) ? p.amen : [];
  const photos = p.photos || [];

  // Auto-resume after Google sign-in:
  // If the user submitted before signing in, we stashed the form state in
  // localStorage and redirected them to login. Now they're back, signed in,
  // and `pendingResume` is the saved state for THIS property. Restore the
  // form, then auto-submit so the wait feels seamless.
  const didAutoSubmit = useRef(false);
  useEffect(() => {
    if (!pendingResume || didAutoSubmit.current || !googleUser) return;
    didAutoSubmit.current = true;
    if (pendingResume.name) setName(pendingResume.name);
    if (pendingResume.phone) setPhone(pendingResume.phone);
    if (pendingResume.message) setMessage(pendingResume.message);
    if (pendingResume.prefRooms != null) setPrefRooms(pendingResume.prefRooms);
    if (pendingResume.prefMaxRent != null) setPrefMaxRent(pendingResume.prefMaxRent);
    if (pendingResume.prefFurnishing) setPrefFurnishing(pendingResume.prefFurnishing);
    if (pendingResume.prefMoveIn) setPrefMoveIn(pendingResume.prefMoveIn);
    // Defer auto-submit by one tick so the setX above commit first.
    setTimeout(() => {
      submitLead({
        name: pendingResume.name || googleUser.name || "",
        phone: pendingResume.phone || "",
        message: pendingResume.message || "",
        prefRooms: pendingResume.prefRooms || 0,
        prefMaxRent: pendingResume.prefMaxRent || "",
        prefFurnishing: pendingResume.prefFurnishing || "any",
        prefMoveIn: pendingResume.prefMoveIn || "",
      });
      // Clear the resume marker so a refresh doesn't double-submit.
      try { localStorage.removeItem("pendingInquiry"); } catch (_) {}
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingResume, googleUser]);

  // Extracted submit so both manual click and pending-resume can reuse it.
  const submitLead = async (fields) => {
    const f = fields || { name, phone, message, prefRooms, prefMaxRent, prefFurnishing, prefMoveIn };
    if (!f.name.trim()) { setError(t.yourName || "Name is required"); return; }
    if (f.phone.replace(/[\s+-]/g, '').length < 10) { setError(t.validPhone || "Valid phone required"); return; }
    setSending(true); setError("");
    const lead = {
      property_id: p.id, name: f.name.trim(), phone: f.phone.trim(), message: f.message.trim(),
      pref_rooms: f.prefRooms || null, pref_max_rent: f.prefMaxRent ? parseInt(f.prefMaxRent) : null,
      pref_furnishing: f.prefFurnishing !== "any" ? f.prefFurnishing : null, pref_move_in: f.prefMoveIn || null,
      social_user_id: googleUser?.id || null,
      website,  // honeypot — empty unless a bot filled it
    };
    const res = await api.submitLead(lead);
    setSending(false);
    if (res.success) setSubmitted(true);
    else if (res.code === 'sign_in_required' || res.code === 'invalid_social_user') {
      // Server confirmed the sign-in is missing/invalid — show the modal
      // even if the client thought googleUser was set (e.g., expired session).
      stashPendingInquiry();
      setShowSignInModal(true);
    } else {
      setError(res.message || (t.inquiryFail || "Failed to submit"));
    }
  };

  // Save current form state so PublicBrowsePage can auto-resume after
  // the user signs in with Google. Property id is part of the payload
  // so we know which property to re-open.
  const stashPendingInquiry = () => {
    try {
      localStorage.setItem("pendingInquiry", JSON.stringify({
        property_id: p.id,
        name: name.trim(),
        phone: phone.trim(),
        message: message.trim(),
        prefRooms, prefMaxRent, prefFurnishing, prefMoveIn,
        savedAt: Date.now(),
      }));
    } catch (_) { /* localStorage full or disabled — modal still shows */ }
  };

  const handleSubmit = async () => {
    // Gate on Google sign-in. If absent, stash the form state and surface
    // the sign-in modal — server-side enforcement is the real guarantee,
    // but blocking at the client level avoids an unnecessary 401 round-trip.
    if (!googleUser) {
      if (!name.trim() && phone.replace(/[\s+-]/g, '').length < 10) {
        // No data entered yet — show modal without stashing empty form.
      } else {
        stashPendingInquiry();
      }
      setShowSignInModal(true);
      return;
    }
    await submitLead();
  };

  return <div style={{ minHeight: "100vh", background: h.bg, padding: 16, maxWidth: 430, margin: "0 auto" }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}>
        <Ic n="back" s={16} c={h.ac} />{t.back}
      </button>
      <HeartBtn isFav={isFav} onToggle={(e) => toggleFav(p.id, e)} h={h} size={24} />
    </div>

    {/* Fullscreen Photo Viewer */}
    {fullscreen && photos.length > 0 && <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.95)", zIndex: 99999, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <button onClick={() => setFullscreen(false)} style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", width: 36, height: 36, borderRadius: "50%", fontSize: 18, cursor: "pointer", zIndex: 2 }}>{"\u2715"}</button>
      <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{photoIdx + 1} / {photos.length}</div>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", width: "100%", position: "relative" }}>
        {photos.length > 1 && <button onClick={() => setPhotoIdx(i => i > 0 ? i - 1 : photos.length - 1)} style={{ position: "absolute", left: 8, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", width: 40, height: 40, borderRadius: "50%", fontSize: 20, cursor: "pointer", zIndex: 2 }}>{"\u2039"}</button>}
        <img src={photos[photoIdx]} alt="" style={{ maxWidth: "90%", maxHeight: "80vh", objectFit: "contain", borderRadius: 8 }} />
        {photos.length > 1 && <button onClick={() => setPhotoIdx(i => i < photos.length - 1 ? i + 1 : 0)} style={{ position: "absolute", right: 8, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", width: 40, height: 40, borderRadius: "50%", fontSize: 20, cursor: "pointer", zIndex: 2 }}>{"\u203A"}</button>}
      </div>
      <div style={{ display: "flex", gap: 6, padding: 16, overflowX: "auto", maxWidth: "100%" }}>
        {photos.map((url, i) => <img key={i} src={url} alt="" onClick={() => setPhotoIdx(i)} style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 6, border: `2px solid ${photoIdx === i ? "#fff" : "rgba(255,255,255,0.2)"}`, cursor: "pointer", flexShrink: 0 }} />)}
      </div>
    </div>}

    {/* Photos */}
    {photos.length > 0 ? (
      <div style={{ borderRadius: 12, overflow: "hidden", marginBottom: 12 }}>
        <div onClick={() => { setPhotoIdx(0); setFullscreen(true); }} style={{ position: "relative", cursor: "pointer" }}>
          <img src={photos[photoIdx]} alt="" style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }} />
          <div style={{ position: "absolute", bottom: 8, right: 8, background: "rgba(0,0,0,0.6)", color: "#fff", padding: "4px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{photoIdx + 1}/{photos.length} {"\uD83D\uDD0D"}</div>
        </div>
        {photos.length > 1 && <div style={{ display: "flex", gap: 6, padding: 8, background: h.cd, overflowX: "auto" }}>
          {photos.map((url, i) => <img key={i} src={url} alt="" onClick={() => setPhotoIdx(i)} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: `3px solid ${photoIdx === i ? h.ac : h.bd}`, cursor: "pointer", flexShrink: 0, transition: "border-color 0.2s" }} />)}
        </div>}
      </div>
    ) : (
      <div style={{ height: 120, borderRadius: 12, background: `${h.ac}08`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
        <Ic n="home" s={40} c={h.tt || h.sc} />
      </div>
    )}

    {/* Property info */}
    {(() => {
      const isComm = isCommercial(p.property_type);
      const tc = isComm ? COMM_COLOR : RES_COLOR;
      return <div style={{ ...crd(h), marginBottom: 16, borderLeft: `3px solid ${tc}` }}>
      {p.org_name && <div style={{ color: h.ac, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>{(t.listedBy || "Listed by")} {p.org_name}</div>}
      {Number(p.ll_count) > 0 && <div style={{ marginBottom: 8 }}><RatingSummary avg={Number(p.ll_avg) || 0} count={Number(p.ll_count) || 0} h={h} size={13} /></div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, gap: 6 }}>
        <h2 style={{ color: h.tx, fontSize: 16, fontWeight: 700, margin: 0 }}>{formatPropAddr(p)}</h2>
        <span style={{ padding: "3px 10px", borderRadius: 10, background: `${h.gn}15`, color: h.gn, fontSize: 10, fontWeight: 600, whiteSpace: "nowrap" }}>{t.available || "Available"}</span>
      </div>
      {/* "X km from you" — only when the user has granted location AND
          the property has coords. p._distance_km is set upstream in
          PublicBrowsePage's `vacant` useMemo. */}
      {p._distance_km != null && Number.isFinite(p._distance_km) && (
        <div style={{ color: h.ac, fontSize: 12, fontWeight: 600, marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
          <span>{"📍"}</span>
          <span>{formatDistance(p._distance_km)} {t.fromYou || "from you"}</span>
        </div>
      )}
      <div style={{ marginBottom: 10 }}>
        <span style={{ background: tc, color: "#fff", padding: "4px 12px", borderRadius: rad.pill, fontSize: 11, fontWeight: 700, display: "inline-block" }}>
          {isComm ? "🏢 " : "🏠 "}{typeLabel(p.property_type)}
        </span>
      </div>
      {p.match_score > 0 && <MatchBadge score={p.match_score} h={h} size="lg" reasons={p.match_reasons} />}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <div style={{ background: `${tc}12`, borderRadius: 10, padding: 10, textAlign: "center" }}>
          <div style={{ color: h.sc, fontSize: 10 }}>{t.rent || "Rent"}</div>
          <div style={{ color: tc, fontWeight: 700, fontSize: 18 }}>₹{(p.rent || 0).toLocaleString()}</div>
          <div style={{ color: h.sc, fontSize: 9 }}>{t.perMonth || "/month"}</div>
        </div>
        <div style={{ background: `${h.ac}08`, borderRadius: 10, padding: 10, textAlign: "center" }}>
          <div style={{ color: h.sc, fontSize: 10 }}>{t.deposit || "Deposit"}</div>
          <div style={{ color: h.tx, fontWeight: 700, fontSize: 18 }}>₹{(p.dep || 0).toLocaleString()}</div>
        </div>
      </div>
      <div style={{ color: h.sc, fontSize: 12, marginBottom: 8 }}>
        {p.rooms} {t.rooms || "Rooms"}{p.area_sqft ? " · " + p.area_sqft + " sqft" : ""}{p.furnishing && p.furnishing !== "unfurnished" ? " · " + p.furnishing : ""}{p.parking ? " · " + p.parking : ""}
      </div>
      {p.match_reasons && p.match_reasons.length > 0 && <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
        {p.match_reasons.map((r, i) => <span key={i} style={{ background: `${h.gn}10`, color: h.gn, padding: "2px 8px", borderRadius: 10, fontSize: 10 }}>{r}</span>)}
      </div>}
      {/* Landlord's marketing description — full text on the detail view */}
      {p.description && <div style={{ marginBottom: 8 }}>
        <div style={{ color: h.sc, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{t.aboutProperty || "About this property"}</div>
        <div style={{ color: h.tx, fontSize: 12.5, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{p.description}</div>
      </div>}
      {amen.length > 0 && <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {amen.map(a => <span key={a} style={{ background: `${h.ac}10`, color: h.ac, padding: "2px 8px", borderRadius: 10, fontSize: 10 }}>{a}</span>)}
      </div>}
    </div>;
    })()}

    {/* Landlord reputation — lets a prospect vet the owner before inquiring */}
    {p.org_id && <LandlordReviews orgId={p.org_id} h={h} t={t} />}

    {/* Inquiry form */}
    {submitted ? <div style={{ ...crd(h), textAlign: "center", padding: 24 }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
      <div style={{ color: h.gn, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{t.inquirySubmitted || "Inquiry Sent!"}</div>
      <div style={{ color: h.sc, fontSize: 13 }}>{t.inquirySubmittedDesc || "The landlord will contact you soon."}</div>
      <button onClick={onBack} style={{ ...btnP(h), marginTop: 16 }}>{t.browseMore || "Browse More Properties"}</button>
    </div> : <div style={crd(h)}>
      <h3 style={{ color: h.tx, fontSize: 14, fontWeight: 700, margin: "0 0 12px" }}>{t.inquireNow || "Inquire Now"}</h3>
      {error && <div style={{ background: `${h.rd}15`, color: h.rd, padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, marginBottom: 12 }}>{error}</div>}
      <label style={{ color: h.sc, fontSize: 11, fontWeight: 500, display: "block", marginBottom: 4 }}>{t.yourName || "Your Name"} *</label>
      <input value={name} onChange={e => setName(e.target.value)} placeholder={t.yourName || "Your Name"} style={inpS(h)} />
      <label style={{ color: h.sc, fontSize: 11, fontWeight: 500, display: "block", marginBottom: 4 }}>{t.yourPhone || "Your Phone"} *</label>
      <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="9876543210" type="tel" style={inpS(h)} />
      <label style={{ color: h.sc, fontSize: 11, fontWeight: 500, display: "block", marginBottom: 4 }}>{t.yourMessage || "Message (optional)"}</label>
      <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder={t.yourMessage || "I'm interested in this property..."} rows={3} style={{ ...inpS(h), resize: "vertical" }} />

      {/* Preferences section */}
      <div style={{ borderTop: `1px solid ${h.bd}`, marginTop: 12, paddingTop: 12 }}>
        <div style={{ color: h.tx, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{t.yourPreferences || "Your Preferences"} <span style={{ color: h.sc, fontWeight: 400, fontSize: 10 }}>({t.optional || "optional"})</span></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <div style={{ color: h.sc, fontSize: 10, marginBottom: 2 }}>{t.prefRooms || "Rooms needed"}</div>
            <select value={prefRooms} onChange={e => setPrefRooms(parseInt(e.target.value))} style={{ ...inpS(h), padding: "6px 8px", fontSize: 11, margin: 0 }}>
              <option value={0}>{t.any || "Any"}</option>
              <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
            </select>
          </div>
          <div>
            <div style={{ color: h.sc, fontSize: 10, marginBottom: 2 }}>{t.maxBudget || "Max Budget"}</div>
            <input value={prefMaxRent} onChange={e => setPrefMaxRent(e.target.value.replace(/\D/g, ''))} placeholder={"\u20B9 Max"} type="text" style={{ ...inpS(h), padding: "6px 8px", fontSize: 11, margin: 0 }} />
          </div>
          <div>
            <div style={{ color: h.sc, fontSize: 10, marginBottom: 2 }}>{t.furnishing || "Furnishing"}</div>
            <select value={prefFurnishing} onChange={e => setPrefFurnishing(e.target.value)} style={{ ...inpS(h), padding: "6px 8px", fontSize: 11, margin: 0 }}>
              <option value="any">{t.any || "Any"}</option>
              <option value="furnished">{t.furnished || "Furnished"}</option>
              <option value="semi">{t.semiFurnished || "Semi"}</option>
              <option value="unfurnished">{t.unfurnished || "Unfurnished"}</option>
            </select>
          </div>
          <div>
            <div style={{ color: h.sc, fontSize: 10, marginBottom: 2 }}>{t.moveIn || "Move-in"}</div>
            <select value={prefMoveIn} onChange={e => setPrefMoveIn(e.target.value)} style={{ ...inpS(h), padding: "6px 8px", fontSize: 11, margin: 0 }}>
              <option value="">{t.any || "Any"}</option>
              <option value="immediate">{t.immediate || "Immediate"}</option>
              <option value="1month">{t.within1Month || "Within 1 month"}</option>
              <option value="flexible">{t.flexible || "Flexible"}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Honeypot — invisible to humans, irresistible to dumb form-fillers.
          Hidden via offscreen positioning + autocomplete off + tabIndex -1
          so screen readers and keyboard users never reach it either.
          If a bot fills this, the server silent-200s and drops the lead. */}
      <input
        type="text"
        name="website"
        value={website}
        onChange={e => setWebsite(e.target.value)}
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
      />

      {/* Sign-in hint banner (only when not signed in) — sets the
          expectation BEFORE the user has typed anything, so the modal
          doesn't come as a surprise on submit. */}
      {!googleUser && (
        <div style={{
          marginTop: 4, marginBottom: 8,
          padding: "8px 12px",
          background: `${h.ac}10`,
          borderRadius: 10,
          border: `1px solid ${h.ac}25`,
          color: h.ac,
          fontSize: 11,
          fontWeight: 600,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span style={{ fontSize: 13 }}>{"🔒"}</span>
          <span>{t.signInToInquireHint || "Sign in with Google to send this inquiry"}</span>
        </div>
      )}

      <button onClick={handleSubmit} disabled={sending} style={{ ...btnP(h), marginTop: 12, opacity: sending ? 0.7 : 1 }}>
        {sending ? "..." : (!googleUser ? (t.signInAndSend || "Sign in & Send Inquiry") : (t.sendInquiry || "Send Inquiry"))}
      </button>
    </div>}

    {/* Sign-in modal — same pattern as the favorites prompt above.
        Clicking "Sign In with Google" takes the user back to the
        landing page where the Login button is; PublicBrowsePage
        watches for the user to return signed-in and auto-resumes
        the saved inquiry from localStorage. */}
    {showSignInModal && <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: h.cd, borderRadius: 16, padding: 24, maxWidth: 320, width: "100%", textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,.2)" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>{"💬"}</div>
        <div style={{ color: h.tx, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{t.signInToInquire || "Sign in to Send Inquiry"}</div>
        <div style={{ color: h.ts, fontSize: 13, marginBottom: 20 }}>{t.signInToInquireDesc || "Sign in with Google so the landlord knows your inquiry is from a verified person — not a bot."}</div>
        <button onClick={() => {
          setShowSignInModal(false);
          if (typeof onRequestSignIn === 'function') onRequestSignIn();
          else onBack(); // fallback — kicks user back to landing for login
        }} style={{ width: "100%", padding: "12px 20px", borderRadius: 12, border: "none", background: h.ac, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 8 }}>
          {t.signInWithGoogle || "Sign In with Google"}
        </button>
        <button onClick={() => setShowSignInModal(false)} style={{ width: "100%", padding: "10px 20px", borderRadius: 12, border: `1px solid ${h.bd}`, background: "transparent", color: h.ts, fontSize: 13, cursor: "pointer" }}>
          {t.maybeLater || "Maybe Later"}
        </button>
      </div>
    </div>}
  </div>;
}

export default PublicBrowsePage;
